<template>
  <div class="shadow-md group relative transition-all duration-200 " :class="bubbleClass">
    <button
      v-if="message.content.type !== MessageType.INTERACTION"
      ref="menuButton"
      type="button"
      @click="toggleMenu"
      class="absolute -top-1.5 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 rounded-full bg-[var(--p-surface-0)] dark:bg-[var(--p-surface-900)] shadow-md hover:shadow-lg hover:bg-[var(--p-surface-50)] dark:hover:bg-[var(--p-surface-800)] border border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] hover:scale-110 active:scale-95"
      :class="isOutgoing ? 'right-2' : 'left-2'"
      :aria-label="t('whatsapp.message_item.message_options')"
      aria-haspopup="true"
      :title="t('whatsapp.message_item.options')"
    >
      <MoreVertical
        :size="14"
        class="text-[var(--p-surface-700)] dark:text-[var(--p-surface-300)]"
      />
    </button>

    <Menu ref="menu" :model="menuItems" :popup="true" class="text-sm">
      <template #item="{ item }">
        <button
          v-if="!item.separator"
          type="button"
          @click="handleMenuItemClick(item)"
          class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-800)] cursor-pointer transition-all duration-150 text-[var(--p-text-color)] active:scale-95"
          :class="item.icon === 'lucide-trash' ? 'hover:text-red-600 dark:hover:text-red-400' : ''"
        >
          <Reply v-if="item.icon === 'lucide-reply'" :size="16" />
          <Copy v-if="item.icon === 'lucide-copy'" :size="16" />
          <Trash2 v-if="item.icon === 'lucide-trash'" :size="16" />
          <span class="font-medium">{{ item.label }}</span>
        </button>
        <div
          v-else
          class="border-t border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] my-0.5"
        ></div>
      </template>
    </Menu>

    <div
      :class="
        message.content.type === MessageType.INTERACTION ? '' : 'px-3 pt-1.5 pb-1 flex flex-col'
      "
    >
      <RepliedMessage
        v-if="
          (message.content.context?.meta_msg_id || message.content.context?.id) &&
          message.content.type !== MessageType.INTERACTION
        "
        :reply-to-meta-msg-id="message.content.context.meta_msg_id ?? undefined"
        :reply-to-id="message.content.context.id ?? undefined"
        :from-user-id="message.content.context.from_user_id ?? undefined"
        :is-sent="isOutgoing"
      />
      <MessageContent
        :content="message.content"
        :source-type="sourceType"
        :message-id="message.id"
        :translation-enabled="translationEnabled"
        :translation-language="translationLanguage"
        :translation-loading="translationLoading"
        @retry-translation="$emit('retry-translation', message)"
      />

      <!-- Hora e indicador integrados debajo del texto (evita superposición vertical) -->
      <div
        v-if="message.content.type !== MessageType.INTERACTION"
        class="flex items-center justify-end gap-1 select-none leading-none self-end"
      >
        <span
          class="text-[9px] font-medium opacity-90"
          :class="
            isOutgoing
              ? 'text-[var(--p-primary-50)] dark:text-[var(--p-primary-100)]'
              : 'text-[var(--p-surface-600)] dark:text-[var(--p-surface-300)]'
          "
        >
          {{ formatTime(message.date) }}
        </span>
        <MessageStatus
          v-if="isOutgoing"
          :status="message.status || 'PENDING'"
          :sent="true"
          class="scale-80 origin-bottom-right"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { MoreVertical, Reply, Copy, Trash2 } from 'lucide-vue-next'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import type { Message } from '../../types/interfaces'
import { MessageSourceType, MessageType } from '../../types/enums'
import MessageContent from './MessageContent.vue'
import MessageStatus from '../../atoms/message/MessageStatus.vue'
import RepliedMessage from '../../molecules/message/RepliedMessage.vue'
import { parseUtcDate } from '../../composables/useTimeFormatter'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

interface Props {
  message: Message
  sourceType: MessageSourceType
  translationEnabled?: boolean
  translationLanguage?: string
  translationLoading?: boolean
}

const props = defineProps<Props>()
const { t, locale } = useI18n()

const isOutgoing = computed(
  () =>
    props.sourceType === MessageSourceType.SALIENTE ||
    props.sourceType === MessageSourceType.TEMPLATE,
)

const isAudioMessage = computed(() => props.message.content.type === MessageType.AUDIO)

const bubbleClass = computed(() => {
  if (props.message.content.type === MessageType.INTERACTION) {
    return 'app-border-card-class shadow-sm text-[var(--p-primary-800)] dark:text-[var(--p-primary-100)] rounded-full px-3 py-1 text-center text-[10px] font-semibold border border-[var(--p-primary-200)] dark:border-[var(--p-primary-800)]/50'
  }

  const colorClass = isOutgoing.value
    ? 'glass-outgoing text-[var(--p-surface-0)] dark:text-[var(--p-primary-0)] rounded-[18px] rounded-tr-[4px] shadow-md'
    : 'app-card-glass backdrop-blur-sm dark:text-[var(--p-surface-50)] dark:border-[var(--p-surface-600)] shadow-[var(--p-surface-900)]/5 rounded-[18px] rounded-tl-[4px]'

  const widthClass = isAudioMessage.value
    ? 'w-[min(72vw,460px)] min-w-[280px] max-w-full'
    : 'w-fit max-w-full'

  return `${colorClass} ${widthClass}`
})

const emit = defineEmits<{
  reply: [message: Message]
  delete: [message: Message]
  'retry-translation': [message: Message]
}>()

const formatTime = (date: string) => {
  const parsedDate = parseUtcDate(date)
  if (Number.isNaN(parsedDate.getTime())) return ''

  const now = new Date()

  const messageDay = new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  ).getTime()
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const dayDiff = Math.floor((todayDay - messageDay) / (1000 * 60 * 60 * 24))

  const timeLabel = parsedDate.toLocaleTimeString(locale.value === 'en' ? 'en-US' : 'es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  if (dayDiff <= 0) {
    return timeLabel
  }

  if (dayDiff === 1) {
    return `${t('whatsapp.message_item.yesterday')} ${timeLabel}`
  }

  const dateLabel = parsedDate.toLocaleDateString(locale.value === 'en' ? 'en-US' : 'es-ES', {
    day: '2-digit',
    month: '2-digit',
  })

  return `${dateLabel} ${timeLabel}`
}
const toast = useToast()

const menu = ref()
const menuButton = ref()

const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [
    {
      label: t('whatsapp.message_item.reply'),
      icon: 'lucide-reply',
      command: () => {
        emit('reply', props.message)
        menu.value?.hide()
      },
    },
  ]

  if (props.message.content.type === MessageType.TEXT) {
    items.push({
      label: t('whatsapp.message_item.copy_text'),
      icon: 'lucide-copy',
      command: () => {
        const text = props.message.content.content || props.message.content.text || ''
        navigator.clipboard.writeText(text).then(() => {
          toast.add({
            severity: 'success',
            summary: t('whatsapp.message_item.text_copied'),
            life: 2000,
          })
        })
        menu.value?.hide()
      },
    })
  }

  items.push({ separator: true })

  /* items.push({
    label: 'Eliminar',
    icon: 'lucide-trash',
    command: () => {
      emit('delete', props.message)
      menu.value?.hide()
    },
  }) */

  return items
})

const toggleMenu = (event: Event) => {
  menu.value.toggle(event)
}

const handleMenuItemClick = (item: MenuItem) => {
  if (item.command) {
    item.command({
      originalEvent: new Event('click'),
      item: item,
    })
  }
}
</script>

<style scoped>
.glass-outgoing {
  /* border: 1px solid color-mix(in srgb, rgba(255, 255, 255, 0.288) 42%, var(--p-primary-color)); */
  background:
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--p-primary-color) 80%, white),
      color-mix(in srgb, var(--p-primary-color) 85%, transparent) 10%,
      color-mix(in srgb, var(--p-primary-color) 72%, #e879f9)
    );
  backdrop-filter: blur(6px) saturate(135%);
  -webkit-backdrop-filter: blur(6px) saturate(135%);
  box-shadow:
    0 2px 8px color-mix(in srgb, var(--p-primary-color) 24%, transparent),
    inset 1px 1px 0 rgba(255, 255, 255, 0.226),
    /* inset 0 0 0 1px rgb(255 255 255 / 22%), */
    inset 0 -8px 16px rgb(255 255 255 / 12%);
}

html.dark .glass-outgoing {
  background:
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--p-primary-800) 82%, var(--p-surface-950)),
      color-mix(in srgb, var(--p-primary-700) 72%, var(--p-surface-900)) 52%,
      color-mix(in srgb, var(--p-primary-900) 78%, #7c3aed)
    );
  border: 1px solid color-mix(in srgb, var(--p-primary-300) 28%, transparent);
  box-shadow:
    0 2px 10px rgb(0 0 0 / 28%),
    inset 1px 1px 0 rgb(255 255 255 / 12%),
    inset 0 -8px 16px rgb(0 0 0 / 14%);
}
</style>
